import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Newspaper, Bell, Warning, ClockCounterClockwise, Books,
  GraduationCap, ShieldCheck, FileText, ChartLine, Download,
  MagnifyingGlass, CaretRight, CheckCircle, XCircle, ArrowsClockwise,
  Globe, MapPin, Tag, Clock, Link as LinkIcon, ArrowSquareOut,
  Shield, LockKey, Eye, Database, Cpu, Bug, ClipboardText, ListChecks, Gauge,
  Plugs, Lightning, Brain, TrendUp, Heartbeat, Gear, GitBranch, ArrowRight
} from '@phosphor-icons/react';
import { API } from '@/App';
import Sidebar from '@/components/Sidebar';

// Enterprise Integration definitions
const ENTERPRISE_INTEGRATIONS = [
  { id: 'azure_ad', name: 'Azure Active Directory', description: 'User access and identity management', icon: 'azure', color: '#0078D4' },
  { id: 'ms_sentinel', name: 'Microsoft Sentinel', description: 'Security incident and event management (SIEM)', icon: 'sentinel', color: '#0078D4' },
  { id: 'ms_defender', name: 'Microsoft Defender', description: 'Endpoint protection and threat detection', icon: 'defender', color: '#0078D4' },
  { id: 'qualys', name: 'Qualys', description: 'Vulnerability management', icon: 'qualys', color: '#ED1C24' },
  { id: 'servicenow', name: 'ServiceNow', description: 'IT service management and incident tracking', icon: 'servicenow', color: '#81B5A1' }
];

// Status badge component
const StatusBadge = ({ status }) => {
  const statusConfig = {
    'Draft': { color: '#5E5E5E', bg: '#2D2D2D' },
    'Consultation Open': { color: '#3B82F6', bg: '#1E3A5F' },
    'Under Review': { color: '#F5A623', bg: '#3E2A0A' },
    'Approved (Not Yet Effective)': { color: '#8B5CF6', bg: '#2D2560' },
    'Promulgated': { color: '#30A46C', bg: '#0F2E1B' }
  };
  
  const config = statusConfig[status] || statusConfig['Draft'];
  
  return (
    <span 
      className="px-2 py-0.5 rounded-sm text-xs font-medium"
      style={{ color: config.color, backgroundColor: config.bg }}
    >
      {status}
    </span>
  );
};

// News category badge
const CategoryBadge = ({ category }) => {
  const categoryConfig = {
    'SA Regulatory': { color: '#D4AF37', bg: '#3E3A0A', icon: <MapPin size={12} /> },
    'Global Threat': { color: '#E5484D', bg: '#3A1214', icon: <Globe size={12} /> },
    'Industry News': { color: '#3B82F6', bg: '#1E3A5F', icon: <Newspaper size={12} /> },
    'Compliance Update': { color: '#30A46C', bg: '#0F2E1B', icon: <Shield size={12} /> },
    'Incident Report': { color: '#F5A623', bg: '#3E2A0A', icon: <Warning size={12} /> }
  };
  
  const config = categoryConfig[category] || categoryConfig['Industry News'];
  
  return (
    <span 
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-xs font-medium"
      style={{ color: config.color, backgroundColor: config.bg }}
    >
      {config.icon}
      {category}
    </span>
  );
};

// News Article Card
const NewsArticleCard = ({ article, compact = false }) => {
  if (compact) {
    return (
      <a
        href={article.source_url}
        target="_blank"
        rel="noopener noreferrer"
        className="block p-3 bg-background rounded-sm hover:border hover:border-gold transition-all group"
      >
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 bg-gold/10 rounded-sm flex items-center justify-center flex-shrink-0">
            <Newspaper size={20} className="text-gold" />
          </div>
          <div className="flex-1 min-w-0">
            <h4 className="text-sm text-text-primary font-medium line-clamp-2 group-hover:text-gold transition-colors">
              {article.title}
            </h4>
            <div className="flex items-center gap-2 mt-1 text-xs text-text-tertiary">
              <span>{article.source}</span>
              <span>•</span>
              <span>{article.read_time}</span>
            </div>
          </div>
          <ArrowSquareOut size={16} className="text-text-tertiary group-hover:text-gold transition-colors flex-shrink-0" />
        </div>
      </a>
    );
  }

  return (
    <motion.a
      href={article.source_url}
      target="_blank"
      rel="noopener noreferrer"
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="block p-4 bg-background-surface border border-border rounded-sm hover:border-gold transition-all group"
    >
      <div className="flex items-start justify-between mb-2">
        <CategoryBadge category={article.category} />
        <span className="text-xs text-text-tertiary">
          {new Date(article.published_date).toLocaleDateString()}
        </span>
      </div>
      <h3 className="text-sm font-medium text-text-primary group-hover:text-gold transition-colors mb-2">
        {article.title}
      </h3>
      <p className="text-xs text-text-secondary line-clamp-2 mb-3">
        {article.summary}
      </p>
      <div className="flex items-center justify-between text-xs text-text-tertiary">
        <span className="flex items-center gap-1">
          <Newspaper size={12} />
          {article.source}
        </span>
        <span className="flex items-center gap-1">
          <Clock size={12} />
          {article.read_time}
        </span>
      </div>
    </motion.a>
  );
};

// Draft Law Card for Regulatory Updates
const DraftLawCard = ({ law }) => {
  const navigate = useNavigate();
  
  return (
    <div 
      className="p-3 bg-background rounded-sm hover:border hover:border-gold transition-all cursor-pointer"
      onClick={() => navigate('/draft-laws')}
    >
      <div className="flex items-start gap-3">
        <div className="w-8 h-8 bg-gold rounded-sm flex items-center justify-center flex-shrink-0">
          <FileText size={16} className="text-gold-foreground" />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="text-sm text-text-primary font-medium line-clamp-1">
            {law.document_name}
          </h4>
          <div className="flex items-center gap-2 mt-1">
            <StatusBadge status={law.status} />
            {law.deadline && (
              <span className="text-xs text-text-tertiary flex items-center gap-1">
                <Clock size={10} />
                {new Date(law.deadline).toLocaleDateString()}
              </span>
            )}
          </div>
        </div>
        <CaretRight size={16} className="text-text-tertiary flex-shrink-0" />
      </div>
    </div>
  );
};

const ThemePage = ({ theme }) => {
  const navigate = useNavigate();
  const [news, setNews] = useState([]);
  const [draftLaws, setDraftLaws] = useState([]);
  const [stats, setStats] = useState(null);
  const [loadingNews, setLoadingNews] = useState(true);
  const [loadingLaws, setLoadingLaws] = useState(true);
  const [refreshingNews, setRefreshingNews] = useState(false);
  
  // Assessment states (for cybersecurity theme)
  const [cyberAssessment, setCyberAssessment] = useState(null);
  const [ictAssessment, setIctAssessment] = useState(null);
  const [loadingAssessments, setLoadingAssessments] = useState(true);
  const [naviAnalysis, setNaviAnalysis] = useState(null);
  const [riskLogItems, setRiskLogItems] = useState([]);

  const themeConfig = {
    cybersecurity: {
      title: 'Cybersecurity Compliance',
      color: '#3B82F6',
      icon: <ShieldCheck size={32} weight="light" />,
      description: 'Monitor cyber threats, regulatory updates, and security compliance requirements',
      threats: [
        { title: 'Ransomware Alert', description: 'New LockBit variant targeting SA financial sector', type: 'critical' },
        { title: 'Phishing Campaign', description: 'Credential harvesting attacks on banking customers', type: 'high' }
      ],
      keyFrameworks: [
        { name: 'POPIA', description: 'Protection of Personal Information Act' },
        { name: 'ISO 27001', description: 'Information Security Management' },
        { name: 'NIST CSF', description: 'Cybersecurity Framework' }
      ]
    },
    'data-protection': {
      title: 'Data Protection',
      color: '#10B981',
      icon: <Database size={32} weight="light" />,
      description: 'POPIA compliance, data privacy, and personal information management',
      threats: [
        { title: 'Data Breach Risk', description: 'Increased targeting of customer databases', type: 'high' },
        { title: 'Compliance Gap', description: 'POPIA audit deadline approaching', type: 'medium' }
      ],
      keyFrameworks: [
        { name: 'POPIA', description: 'Protection of Personal Information Act' },
        { name: 'GDPR', description: 'General Data Protection Regulation' }
      ]
    },
    'financial-crime': {
      title: 'Financial Crime Prevention',
      color: '#F59E0B',
      icon: <Shield size={32} weight="light" />,
      description: 'AML/CFT compliance, fraud prevention, and FICA requirements',
      threats: [
        { title: 'Money Laundering', description: 'New typologies identified by FIC', type: 'critical' },
        { title: 'Sanctions Risk', description: 'Updated OFAC/UN sanctions lists', type: 'high' }
      ],
      keyFrameworks: [
        { name: 'FICA', description: 'Financial Intelligence Centre Act' },
        { name: 'FATF', description: 'Financial Action Task Force Standards' }
      ]
    },
    'tax-compliance': {
      title: 'Tax Compliance',
      color: '#8B5CF6',
      icon: <FileText size={32} weight="light" />,
      description: 'SARS requirements, tax legislation updates, and filing compliance',
      threats: [],
      keyFrameworks: [
        { name: 'Income Tax Act', description: 'South African Tax Law' },
        { name: 'VAT Act', description: 'Value Added Tax Requirements' }
      ]
    },
    'corporate-governance': {
      title: 'Corporate Governance',
      color: '#EC4899',
      icon: <ShieldCheck size={32} weight="light" />,
      description: 'King V compliance, board governance, and corporate ethics',
      threats: [],
      keyFrameworks: [
        { name: 'King V', description: 'Corporate Governance Code' },
        { name: 'Companies Act', description: 'SA Companies Act 71 of 2008' }
      ]
    },
    'consumer-protection': {
      title: 'Consumer Protection',
      color: '#14B8A6',
      icon: <ShieldCheck size={32} weight="light" />,
      description: 'CPA compliance, TCF requirements, and consumer rights',
      threats: [],
      keyFrameworks: [
        { name: 'CPA', description: 'Consumer Protection Act' },
        { name: 'TCF', description: 'Treating Customers Fairly' }
      ]
    },
    'employment-labour': {
      title: 'Employment & Labour',
      color: '#F97316',
      icon: <ShieldCheck size={32} weight="light" />,
      description: 'BCEA, LRA compliance, and workplace regulations',
      threats: [],
      keyFrameworks: [
        { name: 'BCEA', description: 'Basic Conditions of Employment Act' },
        { name: 'LRA', description: 'Labour Relations Act' }
      ]
    }
  };

  const config = themeConfig[theme] || themeConfig.cybersecurity;

  useEffect(() => {
    fetchData();
  }, [theme]);

  const fetchData = async () => {
    setLoadingNews(true);
    setLoadingLaws(true);
    setLoadingAssessments(true);
    
    try {
      const [newsRes, lawsRes, statsRes] = await Promise.all([
        axios.get(`${API}/theme/${theme}/news`),
        axios.get(`${API}/theme/${theme}/draft-laws`),
        axios.get(`${API}/theme/${theme}/stats`)
      ]);
      
      setNews(newsRes.data.articles || []);
      setDraftLaws(lawsRes.data.draft_laws || []);
      setStats(statsRes.data);
      
      // Fetch assessment data for cybersecurity theme
      if (theme === 'cybersecurity') {
        try {
          const [cyberRes, ictRes, analysisRes, riskLogRes] = await Promise.all([
            axios.get(`${API}/cyber-diagnostics/assessments`).catch(() => ({ data: { assessments: [] } })),
            axios.get(`${API}/ict-assessment/assessments`).catch(() => ({ data: { assessments: [] } })),
            axios.get(`${API}/assessments/navi-analysis`).catch(() => ({ data: {} })),
            axios.get(`${API}/risk-log/cybersecurity`).catch(() => ({ data: { risk_items: [] } }))
          ]);
          
          // Get most recent assessment for each
          if (cyberRes.data.assessments?.length > 0) {
            const latestCyber = cyberRes.data.assessments[0];
            const cyberDetail = await axios.get(`${API}/cyber-diagnostics/assessments/${latestCyber.id}`).catch(() => null);
            setCyberAssessment(cyberDetail?.data?.assessment || latestCyber);
          }
          
          if (ictRes.data.assessments?.length > 0) {
            const latestIct = ictRes.data.assessments[0];
            const ictDetail = await axios.get(`${API}/ict-assessment/assessments/${latestIct.id}`).catch(() => null);
            setIctAssessment(ictDetail?.data?.assessment || latestIct);
          }
          
          // Set NAVI analysis
          setNaviAnalysis(analysisRes.data);
          
          // Set risk log items
          setRiskLogItems(riskLogRes.data.risk_items || []);
        } catch (error) {
          console.log('Assessment fetch error:', error);
        }
      }
    } catch (error) {
      console.error('Failed to load theme data:', error);
    } finally {
      setLoadingNews(false);
      setLoadingLaws(false);
      setLoadingAssessments(false);
    }
  };

  const handleRefreshNews = async () => {
    setRefreshingNews(true);
    try {
      const response = await axios.get(`${API}/theme/${theme}/news`);
      setNews(response.data.articles || []);
      toast.success('News feed refreshed');
    } catch (error) {
      toast.error('Failed to refresh news');
    } finally {
      setRefreshingNews(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-background">
      <Sidebar />
      
      <div className="ml-64 flex-1">
        {/* Theme Header */}
        <div 
          className="p-8 border-b border-border"
          style={{ 
            background: `linear-gradient(135deg, ${config.color}15 0%, ${config.color}05 100%)`,
            borderLeft: `4px solid ${config.color}`
          }}
        >
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-4">
              <div style={{ color: config.color }}>
                {config.icon}
              </div>
              <div>
                <h1 className="text-3xl font-heading font-medium text-text-primary">
                  {config.title}
                </h1>
                <p className="text-text-secondary mt-1">
                  {config.description}
                </p>
              </div>
            </div>
            
            {/* Quick Stats */}
            {stats && (
              <div className="flex items-center gap-4">
                <div className="text-center px-4">
                  <div className="text-2xl font-mono font-semibold text-gold">{stats.total_draft_laws}</div>
                  <div className="text-xs text-text-tertiary">Draft Laws</div>
                </div>
                <div className="w-px h-10 bg-border" />
                <div className="text-center px-4">
                  <div className="text-2xl font-mono font-semibold" style={{ color: '#3B82F6' }}>{stats.open_consultation}</div>
                  <div className="text-xs text-text-tertiary">Open Consultation</div>
                </div>
                {stats.high_risk_items > 0 && (
                  <>
                    <div className="w-px h-10 bg-border" />
                    <div className="text-center px-4">
                      <div className="text-2xl font-mono font-semibold text-risk-high-text">{stats.high_risk_items}</div>
                      <div className="text-xs text-text-tertiary">High Risk</div>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Content Grid */}
        <div className="p-6">
          <div className="grid grid-cols-12 gap-6">
            
            {/* Assessment Tools & Results - Cybersecurity Only */}
            {theme === 'cybersecurity' ? (
              <div className="col-span-8">
                <div className="bg-background-surface border border-gold/30 rounded-sm p-5">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <ClipboardText size={20} className="text-gold" />
                      <h3 className="font-heading font-medium text-text-primary">Assessment Tools</h3>
                    </div>
                    <Link
                      to="/diagnostics"
                      className="text-xs text-gold hover:underline flex items-center gap-1"
                    >
                      Diagnostics Hub
                      <ArrowRight size={12} />
                    </Link>
                  </div>
                  
                  {/* Assessment Cards Grid */}
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    {/* Cyber Diagnostics Card */}
                    <Link
                      to="/diagnostics/cybersecurity"
                      className="group p-4 bg-background rounded-lg border border-border hover:border-blue-500/50 transition-all"
                      data-testid="cyber-diagnostics-card"
                    >
                      <div className="flex items-start gap-3 mb-3">
                        <div className="w-12 h-12 rounded-lg bg-blue-500/10 flex items-center justify-center flex-shrink-0">
                          <Shield size={26} className="text-blue-400" />
                        </div>
                        <div className="flex-1">
                          <h4 className="text-sm font-medium text-text-primary group-hover:text-blue-400 transition-colors">
                            Cyber Diagnostics
                          </h4>
                          <p className="text-xs text-text-tertiary">Joint Standard 1 & 2</p>
                        </div>
                      </div>
                      <p className="text-xs text-text-secondary mb-3">
                        Assess cyber resilience posture with 33 questions across 8 domains
                      </p>
                      <div className="flex items-center justify-between">
                        <span className="px-2 py-1 bg-blue-500/10 text-blue-400 text-xs rounded">
                          33 Questions
                        </span>
                        <span className="text-gold text-xs group-hover:translate-x-1 transition-transform">
                          Start Assessment →
                        </span>
                      </div>
                    </Link>

                    {/* ICT Assessment Card */}
                    <Link
                      to="/diagnostics/ict-assessment"
                      className="group p-4 bg-background rounded-lg border border-border hover:border-cyan-500/50 transition-all"
                      data-testid="ict-assessment-card"
                    >
                      <div className="flex items-start gap-3 mb-3">
                        <div className="w-12 h-12 rounded-lg bg-cyan-500/10 flex items-center justify-center flex-shrink-0">
                          <Cpu size={26} className="text-cyan-400" />
                        </div>
                        <div className="flex-1">
                          <h4 className="text-sm font-medium text-text-primary group-hover:text-cyan-400 transition-colors">
                            ICT Assessment
                          </h4>
                          <p className="text-xs text-text-tertiary">ISA 315 Aligned</p>
                        </div>
                      </div>
                      <p className="text-xs text-text-secondary mb-3">
                        IT General Controls assessment across 7 domains with 45 questions
                      </p>
                      <div className="flex items-center justify-between">
                        <span className="px-2 py-1 bg-cyan-500/10 text-cyan-400 text-xs rounded">
                          45 Questions
                        </span>
                        <span className="text-gold text-xs group-hover:translate-x-1 transition-transform">
                          Start Assessment →
                        </span>
                      </div>
                    </Link>
                  </div>
                  
                  {/* Assessment Results Section */}
                  <div className="border-t border-border pt-4">
                    <div className="flex items-center gap-2 mb-4">
                      <Gauge size={18} className="text-gold" />
                      <h4 className="text-sm font-medium text-text-primary">Assessment Results</h4>
                    </div>
                    
                    {loadingAssessments ? (
                      <div className="grid grid-cols-2 gap-4">
                        {[1, 2].map((i) => (
                          <div key={i} className="p-4 bg-background rounded-lg animate-pulse">
                            <div className="h-4 bg-border rounded w-32 mb-3" />
                            <div className="h-8 bg-border rounded w-20 mb-2" />
                            <div className="h-3 bg-border rounded w-24" />
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="grid grid-cols-2 gap-4">
                        {/* Cyber Assessment Result */}
                        <div className="p-4 bg-background rounded-lg border border-border">
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-2">
                              <Shield size={16} className="text-blue-400" />
                              <span className="text-xs font-medium text-text-primary">Cyber Diagnostics</span>
                            </div>
                            {cyberAssessment && (
                              <span className={`text-xs px-2 py-0.5 rounded ${
                                cyberAssessment.overall_score >= 70 
                                  ? 'bg-emerald-500/10 text-emerald-400' 
                                  : cyberAssessment.overall_score >= 40 
                                    ? 'bg-amber-500/10 text-amber-400'
                                    : 'bg-red-500/10 text-red-400'
                              }`}>
                                {cyberAssessment.maturity_level || 'In Progress'}
                              </span>
                            )}
                          </div>
                          
                          {cyberAssessment ? (
                            <>
                              <div className="flex items-baseline gap-2 mb-2">
                                <span className="text-3xl font-mono font-bold text-blue-400">
                                  {cyberAssessment.overall_score?.toFixed(0) || 0}%
                                </span>
                                <span className="text-xs text-text-tertiary">overall score</span>
                              </div>
                              <div className="w-full h-2 bg-border rounded-full overflow-hidden mb-3">
                                <div 
                                  className="h-full bg-blue-500 transition-all"
                                  style={{ width: `${cyberAssessment.overall_score || 0}%` }}
                                />
                              </div>
                              <div className="flex items-center justify-between text-xs">
                                <span className="text-text-tertiary">
                                  {Object.keys(cyberAssessment.answers || {}).length} / 33 answered
                                </span>
                                <Link 
                                  to="/diagnostics/cybersecurity"
                                  className="text-gold hover:underline"
                                >
                                  View Details →
                                </Link>
                              </div>
                            </>
                          ) : (
                            <div className="text-center py-4">
                              <p className="text-xs text-text-tertiary mb-2">No assessment started</p>
                              <Link 
                                to="/diagnostics/cybersecurity"
                                className="text-xs text-gold hover:underline"
                              >
                                Start Assessment
                              </Link>
                            </div>
                          )}
                        </div>
                        
                        {/* ICT Assessment Result */}
                        <div className="p-4 bg-background rounded-lg border border-border">
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-2">
                              <Cpu size={16} className="text-cyan-400" />
                              <span className="text-xs font-medium text-text-primary">ICT Assessment</span>
                            </div>
                            {ictAssessment && (
                              <span className={`text-xs px-2 py-0.5 rounded ${
                                ictAssessment.overall_score >= 70 
                                  ? 'bg-emerald-500/10 text-emerald-400' 
                                  : ictAssessment.overall_score >= 40 
                                    ? 'bg-amber-500/10 text-amber-400'
                                    : 'bg-red-500/10 text-red-400'
                              }`}>
                                {ictAssessment.maturity_level || 'In Progress'}
                              </span>
                            )}
                          </div>
                          
                          {ictAssessment ? (
                            <>
                              <div className="flex items-baseline gap-2 mb-2">
                                <span className="text-3xl font-mono font-bold text-cyan-400">
                                  {ictAssessment.overall_score?.toFixed(0) || 0}%
                                </span>
                                <span className="text-xs text-text-tertiary">overall score</span>
                              </div>
                              <div className="w-full h-2 bg-border rounded-full overflow-hidden mb-3">
                                <div 
                                  className="h-full bg-cyan-500 transition-all"
                                  style={{ width: `${ictAssessment.overall_score || 0}%` }}
                                />
                              </div>
                              <div className="flex items-center justify-between text-xs">
                                <span className="text-text-tertiary">
                                  {Object.keys(ictAssessment.answers || {}).length} / 45 answered
                                </span>
                                <Link 
                                  to="/diagnostics/ict-assessment"
                                  className="text-gold hover:underline"
                                >
                                  View Details →
                                </Link>
                              </div>
                            </>
                          ) : (
                            <div className="text-center py-4">
                              <p className="text-xs text-text-tertiary mb-2">No assessment started</p>
                              <Link 
                                to="/diagnostics/ict-assessment"
                                className="text-xs text-gold hover:underline"
                              >
                                Start Assessment
                              </Link>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              /* News Section - For non-cybersecurity themes */
              <div className="col-span-8">
                <div className="bg-background-surface border border-border rounded-sm p-5">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <Newspaper size={20} className="text-gold" />
                      <h3 className="font-heading font-medium text-text-primary">Latest News</h3>
                      <span className="text-xs text-text-tertiary ml-2">
                        Curated from SA & global sources
                      </span>
                    </div>
                    <button
                      onClick={handleRefreshNews}
                      disabled={refreshingNews}
                      className="flex items-center gap-2 text-xs text-gold hover:underline disabled:opacity-50"
                    >
                      <ArrowsClockwise size={14} className={refreshingNews ? 'animate-spin' : ''} />
                      Refresh
                    </button>
                  </div>
                  
                  {loadingNews ? (
                    <div className="grid grid-cols-2 gap-4">
                      {[1, 2, 3, 4].map((i) => (
                        <div key={i} className="p-4 bg-background rounded-sm animate-pulse">
                          <div className="h-4 bg-border rounded w-20 mb-3" />
                          <div className="h-4 bg-border rounded w-full mb-2" />
                          <div className="h-4 bg-border rounded w-3/4" />
                        </div>
                      ))}
                    </div>
                  ) : news.length > 0 ? (
                    <div className="grid grid-cols-2 gap-4">
                      {news.slice(0, 6).map((article, idx) => (
                        <NewsArticleCard key={article.id || idx} article={article} />
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-text-tertiary">
                      <Newspaper size={32} className="mx-auto mb-2 opacity-50" />
                      <p className="text-sm">No news articles available</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Regulatory Updates (Draft Laws) - Hidden for Cybersecurity */}
            {theme !== 'cybersecurity' && (
              <div className="col-span-4">
                <div className="bg-background-surface border border-border rounded-sm p-5">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <Bell size={20} className="text-gold" />
                      <h3 className="font-heading font-medium text-text-primary">Regulatory Updates</h3>
                    </div>
                    <button
                      onClick={() => navigate('/draft-laws')}
                      className="text-xs text-gold hover:underline flex items-center gap-1"
                    >
                      View All
                      <CaretRight size={12} />
                    </button>
                  </div>
                  
                  {loadingLaws ? (
                    <div className="space-y-3">
                      {[1, 2, 3].map((i) => (
                        <div key={i} className="p-3 bg-background rounded-sm animate-pulse">
                          <div className="h-4 bg-border rounded w-full mb-2" />
                          <div className="h-3 bg-border rounded w-20" />
                        </div>
                      ))}
                    </div>
                  ) : draftLaws.length > 0 ? (
                    <div className="space-y-3">
                      {draftLaws.slice(0, 5).map((law) => (
                        <DraftLawCard key={law.id} law={law} />
                      ))}
                      {draftLaws.length > 5 && (
                        <button
                          onClick={() => navigate('/draft-laws')}
                          className="w-full text-center text-xs text-gold hover:underline py-2"
                        >
                          +{draftLaws.length - 5} more updates
                        </button>
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <FileText size={32} className="mx-auto mb-2 text-text-tertiary opacity-50" />
                      <p className="text-sm text-text-tertiary mb-3">No draft laws for this theme</p>
                      <button
                        onClick={() => navigate('/draft-laws')}
                        className="text-xs text-gold hover:underline"
                      >
                        Scan for updates
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Threats & Vulnerabilities - Hidden for Cybersecurity */}
            {theme !== 'cybersecurity' && config.threats && config.threats.length > 0 && (
              <div className="col-span-4">
                <div className="bg-background-surface border border-border rounded-sm p-5">
                  <div className="flex items-center gap-2 mb-4">
                    <Warning size={20} className="text-risk-high-text" />
                    <h3 className="font-heading font-medium text-text-primary">Active Threats</h3>
                  </div>
                  <div className="space-y-3">
                    {config.threats.map((threat, idx) => (
                      <div 
                        key={idx} 
                        className={`p-3 rounded-sm border ${
                          threat.type === 'critical' 
                            ? 'bg-risk-high-bg border-risk-high-border' 
                            : 'bg-risk-medium-bg border-risk-medium-border'
                        }`}
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <Warning 
                            size={16} 
                            weight="fill" 
                            className={threat.type === 'critical' ? 'text-risk-high-text' : 'text-risk-medium-text'} 
                          />
                          <span className={`text-sm font-semibold ${
                            threat.type === 'critical' ? 'text-risk-high-text' : 'text-risk-medium-text'
                          }`}>
                            {threat.title}
                          </span>
                        </div>
                        <p className="text-xs text-text-secondary pl-6">
                          {threat.description}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Quick Tools - Cybersecurity Only (Risk Log & Remediation) */}
            {theme === 'cybersecurity' && (
              <div className="col-span-8">
                <div className="bg-background-surface border border-border rounded-sm p-5">
                  <div className="flex items-center gap-2 mb-4">
                    <ListChecks size={20} className="text-gold" />
                    <h3 className="font-heading font-medium text-text-primary">Compliance Tools</h3>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    {/* Risk Log */}
                    <Link
                      to="/risk-log"
                      className="group p-4 bg-background rounded-lg border border-border hover:border-amber-500/50 transition-all"
                      data-testid="cybersecurity-risk-log-link"
                    >
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
                          <Warning size={22} className="text-amber-400" />
                        </div>
                        <div>
                          <h4 className="text-sm font-medium text-text-primary group-hover:text-amber-400 transition-colors">
                            Risk Log
                          </h4>
                          <p className="text-xs text-text-tertiary">Flagged audit items</p>
                        </div>
                      </div>
                      <p className="text-xs text-text-secondary mb-3">
                        Review auto-flagged items from assessments and evidence-failed controls
                      </p>
                      <span className="text-gold text-xs">View Risk Log →</span>
                    </Link>

                    {/* Remediation Tracker */}
                    <Link
                      to="/remediation-tracker"
                      className="group p-4 bg-background rounded-lg border border-border hover:border-emerald-500/50 transition-all"
                      data-testid="cybersecurity-remediation-link"
                    >
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-10 h-10 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                          <ListChecks size={22} className="text-emerald-400" />
                        </div>
                        <div>
                          <h4 className="text-sm font-medium text-text-primary group-hover:text-emerald-400 transition-colors">
                            Remediation Tracker
                          </h4>
                          <p className="text-xs text-text-tertiary">Gap closure workflow</p>
                        </div>
                      </div>
                      <p className="text-xs text-text-secondary mb-3">
                        Track and resolve control gaps and compliance deficiencies
                      </p>
                      <span className="text-gold text-xs">Track Items →</span>
                    </Link>
                  </div>
                </div>
              </div>
            )}
            
            {/* NAVI Analysis Section - Cybersecurity Only */}
            {theme === 'cybersecurity' && (naviAnalysis?.ict_assessment?.navi_analysis || naviAnalysis?.cyber_assessment?.navi_analysis) && (
              <div className="col-span-8">
                <div className="bg-background-surface border border-emerald-500/30 rounded-sm p-5">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <Brain size={20} className="text-emerald-400" />
                      <h3 className="font-heading font-medium text-text-primary">NAVI Analysis</h3>
                      <span className="text-xs text-emerald-400 ml-2">AI-Generated Insights</span>
                    </div>
                  </div>
                  
                  <div className="space-y-4">
                    {naviAnalysis?.cyber_assessment?.navi_analysis && (
                      <div className="bg-background rounded-lg p-4 border border-border">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <Shield size={16} className="text-blue-400" />
                            <span className="text-sm font-medium text-text-primary">Cyber Resilience Assessment</span>
                            <span className={`text-xs px-2 py-0.5 rounded ${
                              (naviAnalysis.cyber_assessment.overall_score || 0) >= 70 
                                ? 'bg-emerald-500/10 text-emerald-400' 
                                : 'bg-amber-500/10 text-amber-400'
                            }`}>
                              {naviAnalysis.cyber_assessment.overall_score?.toFixed(0) || 0}%
                            </span>
                          </div>
                          <span className="text-xs text-text-tertiary">
                            {naviAnalysis.cyber_assessment.analysis_generated_at 
                              ? new Date(naviAnalysis.cyber_assessment.analysis_generated_at).toLocaleDateString()
                              : ''}
                          </span>
                        </div>
                        <div className="text-xs text-text-secondary whitespace-pre-wrap leading-relaxed max-h-48 overflow-y-auto">
                          {naviAnalysis.cyber_assessment.navi_analysis}
                        </div>
                      </div>
                    )}
                    
                    {naviAnalysis?.ict_assessment?.navi_analysis && (
                      <div className="bg-background rounded-lg p-4 border border-border">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <Cpu size={16} className="text-cyan-400" />
                            <span className="text-sm font-medium text-text-primary">ICT Assessment (ISA 315)</span>
                            <span className={`text-xs px-2 py-0.5 rounded ${
                              (naviAnalysis.ict_assessment.overall_score || 0) >= 70 
                                ? 'bg-emerald-500/10 text-emerald-400' 
                                : 'bg-amber-500/10 text-amber-400'
                            }`}>
                              {naviAnalysis.ict_assessment.overall_score?.toFixed(0) || 0}%
                            </span>
                          </div>
                          <span className="text-xs text-text-tertiary">
                            {naviAnalysis.ict_assessment.analysis_generated_at 
                              ? new Date(naviAnalysis.ict_assessment.analysis_generated_at).toLocaleDateString()
                              : ''}
                          </span>
                        </div>
                        <div className="text-xs text-text-secondary whitespace-pre-wrap leading-relaxed max-h-48 overflow-y-auto">
                          {naviAnalysis.ict_assessment.navi_analysis}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
            
            {/* Risk Log Section - Cybersecurity Only */}
            {theme === 'cybersecurity' && riskLogItems.length > 0 && (
              <div className="col-span-4">
                <div className="bg-background-surface border border-red-500/30 rounded-sm p-5">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <Warning size={20} className="text-red-400" />
                      <h3 className="font-heading font-medium text-text-primary">Risk Log</h3>
                      <span className="px-2 py-0.5 bg-red-500/10 text-red-400 text-xs rounded">
                        {riskLogItems.filter(r => r.status === 'open').length} Open
                      </span>
                    </div>
                    <Link
                      to="/risk-log"
                      className="text-xs text-gold hover:underline"
                    >
                      View All →
                    </Link>
                  </div>
                  
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {riskLogItems.slice(0, 8).map((item) => (
                      <div 
                        key={item.id}
                        className={`p-3 rounded border ${
                          item.severity === 'high' 
                            ? 'bg-red-500/5 border-red-500/30' 
                            : 'bg-amber-500/5 border-amber-500/30'
                        }`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <span className={`w-2 h-2 rounded-full ${
                                item.severity === 'high' ? 'bg-red-500' : 'bg-amber-500'
                              }`} />
                              <span className="text-xs text-text-tertiary truncate">
                                {item.domain_name || item.domain}
                              </span>
                            </div>
                            <p className="text-xs text-text-secondary line-clamp-2">
                              {item.question}
                            </p>
                          </div>
                          <span className={`text-xs font-mono px-1.5 py-0.5 rounded ${
                            item.score <= 1 ? 'bg-red-500/10 text-red-400' : 'bg-amber-500/10 text-amber-400'
                          }`}>
                            {item.score}/4
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  {riskLogItems.length > 8 && (
                    <Link
                      to="/risk-log"
                      className="block mt-3 text-center text-xs text-gold hover:underline"
                    >
                      +{riskLogItems.length - 8} more items
                    </Link>
                  )}
                </div>
              </div>
            )}

            {/* Enterprise Integrations - Cybersecurity Only */}
            {theme === 'cybersecurity' && (
              <div className="col-span-8">
                <div className="bg-background-surface border border-border rounded-sm p-5">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <Plugs size={20} className="text-gold" />
                      <h3 className="font-heading font-medium text-text-primary">Enterprise Integrations</h3>
                      <span className="text-xs text-text-tertiary ml-2">Connect your security tools for automated data collection</span>
                    </div>
                    <Link
                      to="/settings/integrations"
                      className="flex items-center gap-1 text-xs text-gold hover:underline"
                    >
                      <Gear size={14} />
                      Manage Integrations
                    </Link>
                  </div>
                  <div className="grid grid-cols-5 gap-3">
                    {ENTERPRISE_INTEGRATIONS.map((integration) => (
                      <Link
                        key={integration.id}
                        to="/settings/integrations"
                        className="group p-3 bg-background border border-border rounded-lg hover:border-gold/50 transition-all"
                        data-testid={`integration-${integration.id}`}
                      >
                        <div className="flex items-center gap-2 mb-2">
                          <div 
                            className="w-6 h-6 rounded flex items-center justify-center"
                            style={{ backgroundColor: `${integration.color}20` }}
                          >
                            <Plugs size={14} style={{ color: integration.color }} />
                          </div>
                          <span className="text-xs font-medium text-text-primary group-hover:text-gold transition-colors truncate">
                            {integration.name}
                          </span>
                        </div>
                        <p className="text-[10px] text-text-tertiary line-clamp-2">{integration.description}</p>
                        <div className="mt-2 flex items-center justify-between">
                          <span className="text-[10px] text-text-tertiary">Not Connected</span>
                          <span className="w-2 h-2 rounded-full bg-gray-500"></span>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Audit Intelligence - NAVI Powered - Cybersecurity Only */}
            {theme === 'cybersecurity' && (
              <div className="col-span-4">
                <div className="bg-background-surface border border-purple-500/30 rounded-sm p-5">
                  <div className="flex items-center gap-2 mb-4">
                    <Brain size={20} className="text-purple-400" />
                    <h3 className="font-heading font-medium text-text-primary">Audit Intelligence</h3>
                    <span className="ml-auto px-2 py-0.5 bg-purple-500/10 text-purple-400 text-xs rounded flex items-center gap-1">
                      <Lightning size={10} />
                      NAVI Powered
                    </span>
                  </div>
                  
                  <p className="text-xs text-text-tertiary mb-4">
                    AI-driven insights from gaps, breaches, incidents, and industry trends
                  </p>

                  {/* Insight Categories */}
                  <div className="space-y-2 mb-4">
                    <div className="flex items-center justify-between p-2 bg-background rounded">
                      <div className="flex items-center gap-2">
                        <Warning size={14} className="text-red-400" />
                        <span className="text-xs text-text-secondary">High-Risk Incidents</span>
                      </div>
                      <span className="text-xs font-mono text-red-400">0</span>
                    </div>
                    <div className="flex items-center justify-between p-2 bg-background rounded">
                      <div className="flex items-center gap-2">
                        <Heartbeat size={14} className="text-amber-400" />
                        <span className="text-xs text-text-secondary">Active Breaches</span>
                      </div>
                      <span className="text-xs font-mono text-amber-400">0</span>
                    </div>
                    <div className="flex items-center justify-between p-2 bg-background rounded">
                      <div className="flex items-center gap-2">
                        <XCircle size={14} className="text-orange-400" />
                        <span className="text-xs text-text-secondary">Non-Compliance Logs</span>
                      </div>
                      <span className="text-xs font-mono text-orange-400">0</span>
                    </div>
                    <div className="flex items-center justify-between p-2 bg-background rounded">
                      <div className="flex items-center gap-2">
                        <TrendUp size={14} className="text-blue-400" />
                        <span className="text-xs text-text-secondary">Industry Trends</span>
                      </div>
                      <span className="text-xs font-mono text-blue-400">3</span>
                    </div>
                  </div>

                  {/* NAVI Insight Preview */}
                  <div className="p-3 bg-purple-500/5 border border-purple-500/20 rounded-lg mb-3">
                    <div className="flex items-start gap-2">
                      <Brain size={16} className="text-purple-400 mt-0.5" />
                      <div>
                        <p className="text-xs text-text-secondary">
                          <span className="text-purple-400 font-medium">NAVI Insight:</span> Connect your enterprise tools to receive AI-powered audit recommendations based on real-time security data.
                        </p>
                      </div>
                    </div>
                  </div>

                  <Link
                    to="/audit-intelligence"
                    className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-purple-500/10 border border-purple-500/30 text-purple-400 rounded-sm hover:bg-purple-500/20 transition-all text-sm"
                  >
                    <Brain size={16} />
                    Open Audit Intelligence
                  </Link>
                </div>
              </div>
            )}

            {/* Key Frameworks */}
            <div className={`${config.threats && config.threats.length > 0 ? 'col-span-4' : 'col-span-4'}`}>
              <div className="bg-background-surface border border-border rounded-sm p-5">
                <div className="flex items-center gap-2 mb-4">
                  <Books size={20} className="text-gold" />
                  <h3 className="font-heading font-medium text-text-primary">Key Frameworks</h3>
                </div>
                <div className="space-y-3">
                  {config.keyFrameworks.map((framework, idx) => (
                    <div key={idx} className="p-3 bg-background rounded-sm">
                      <div className="flex items-center gap-2 mb-1">
                        <Shield size={16} className="text-gold" />
                        <span className="text-sm font-semibold text-gold">{framework.name}</span>
                      </div>
                      <p className="text-xs text-text-secondary pl-6">
                        {framework.description}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Compliance Dashboard */}
            <div className="col-span-4">
              <div className="bg-background-surface border border-border rounded-sm p-5">
                <h3 className="font-heading font-medium text-text-primary mb-4">
                  Compliance Dashboard
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 bg-background rounded-sm">
                    <div className="flex items-baseline gap-2 mb-1">
                      <span className="text-3xl font-mono font-semibold text-gold">
                        {stats?.total_draft_laws || 0}
                      </span>
                    </div>
                    <span className="text-xs text-text-tertiary">Items Tracked</span>
                  </div>
                  <div className="p-4 bg-background rounded-sm">
                    <div className="flex items-center gap-2 mb-1">
                      <Warning size={20} className="text-risk-high-text" />
                      <span className="text-3xl font-mono font-semibold text-risk-high-text">
                        {stats?.high_risk_items || 0}
                      </span>
                    </div>
                    <span className="text-xs text-text-tertiary">High Risk</span>
                  </div>
                  <div className="p-4 bg-background rounded-sm col-span-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-text-tertiary">Open Consultations</span>
                      <span className="text-lg font-semibold" style={{ color: config.color }}>
                        {stats?.open_consultation || 0}
                      </span>
                    </div>
                  </div>
                </div>
                <button 
                  onClick={() => navigate('/draft-laws')}
                  className="w-full mt-4 flex items-center justify-center gap-2 px-4 py-2 bg-gold hover:bg-gold-hover text-gold-foreground rounded-sm transition-all"
                >
                  <FileText size={16} />
                  <span className="text-sm">Manage Draft Laws</span>
                </button>
              </div>
            </div>

            {/* More News - Compact List (Non-cybersecurity themes only) */}
            {theme !== 'cybersecurity' && news.length > 6 && (
              <div className="col-span-4">
                <div className="bg-background-surface border border-border rounded-sm p-5">
                  <div className="flex items-center gap-2 mb-4">
                    <Newspaper size={20} className="text-gold" />
                    <h3 className="font-heading font-medium text-text-primary">More Headlines</h3>
                  </div>
                  <div className="space-y-2">
                    {news.slice(6, 10).map((article, idx) => (
                      <NewsArticleCard key={article.id || idx} article={article} compact />
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Quick Actions */}
            <div className="col-span-4">
              <div className="bg-background-surface border border-border rounded-sm p-5">
                <h3 className="font-heading font-medium text-text-primary mb-4">
                  Quick Actions
                </h3>
                <div className="space-y-2">
                  <button 
                    onClick={() => navigate('/draft-laws')}
                    className="w-full flex items-center justify-between p-3 bg-background rounded-sm hover:border hover:border-gold transition-all text-left"
                  >
                    <div className="flex items-center gap-2">
                      <MagnifyingGlass size={16} className="text-gold" />
                      <span className="text-sm text-text-primary">Scan for New Regulations</span>
                    </div>
                    <CaretRight size={16} className="text-text-tertiary" />
                  </button>
                  <button 
                    onClick={() => navigate('/compliance-alerts')}
                    className="w-full flex items-center justify-between p-3 bg-background rounded-sm hover:border hover:border-gold transition-all text-left"
                  >
                    <div className="flex items-center gap-2">
                      <Bell size={16} className="text-gold" />
                      <span className="text-sm text-text-primary">View Compliance Alerts</span>
                    </div>
                    <CaretRight size={16} className="text-text-tertiary" />
                  </button>
                  <button 
                    onClick={() => navigate('/navi')}
                    className="w-full flex items-center justify-between p-3 bg-background rounded-sm hover:border hover:border-gold transition-all text-left"
                  >
                    <div className="flex items-center gap-2">
                      <ShieldCheck size={16} className="text-gold" />
                      <span className="text-sm text-text-primary">Ask NAVI About {config.title.split(' ')[0]}</span>
                    </div>
                    <CaretRight size={16} className="text-text-tertiary" />
                  </button>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default ThemePage;
